/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IThemeProvider } from "@adapptio/lib-core";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners
} from "@adapptio/lib-shared";
import { AppConfigService } from "../config/AppConfigService";
import { IEditorApiService } from "../api/IEditorApiService";
import { ResourceService } from "../resources/ResourceService";
import { IEditorResource_Theme } from "../resources/ResourceTypes";
import { isResourceTheme } from "../resources/ResourceHelpers";

export interface TEditorThemeProviderConfig {
	appConfigService: AppConfigService;
	apiService: IEditorApiService;
	resourceService: ResourceService;
}

/**
 * Provides theme and handles reload
 */
export class EditorThemeProvider implements IThemeProvider {
	private readonly apiSvc: IEditorApiService;
	private readonly appConfigSvc: AppConfigService;
	private readonly resourceService: ResourceService;

	private themeId: string | null = null;
	private styleName: string | null = null;
	private themeResource: IEditorResource_Theme | null = null;

	public readonly onChange = createEventEmitter();

	/**
	 * Constructor
	 */
	public constructor(config: TEditorThemeProviderConfig) {
		this.apiSvc = config.apiService;
		this.appConfigSvc = config.appConfigService;
		this.resourceService = config.resourceService;

		this.loadAppConfig();

		onEvent(this.appConfigSvc.onChange, this.handleAppConfigChange);
	}

	private readonly handleAppConfigChange = (): void => {
		this.loadAppConfig();
	};

	private loadAppConfig(): void {
		const { themeId, styleName } = this.appConfigSvc.getConfig()?._app?.theme || {
			themeId: null,
			styleName: null
		};

		if (themeId !== this.themeId || styleName !== this.styleName) {
			this.themeId = themeId;
			this.styleName = styleName;
			this.reloadThemeResource();
		}
	}

	private async reloadThemeResource(): Promise<void> {
		this.clearThemeResource();

		if (this.themeId) {
			await this.resourceService.waitForInit();

			const themeResource = this.resourceService.getResourceById(this.themeId);

			if (isResourceTheme(themeResource)) {
				this.setThemeResource(themeResource);
				return;
			}
		}
	}

	private setThemeResource(themeResource: IEditorResource_Theme): void {
		this.clearThemeResource();

		this.themeResource = themeResource;
		onEvent(this.themeResource.onChange, this.handleThemeResourceChange);
	}

	private clearThemeResource(): void {
		if (this.themeResource) {
			offEvent(this.themeResource.onChange, this.handleThemeResourceChange);
			this.themeResource = null;
		}
	}

	private handleThemeResourceChange = (): void => {
		this.emitChange();
	};

	private emitChange(): void {
		emitEvent(this.onChange);
	}

	public getThemeCssUrl(): string | null {
		if (!this.themeId) {
			return null;
		}

		const url = new URL(this.apiSvc.getThemeCssUrl({ themeId: this.themeId, styleName: this.styleName }));

		const version = `${
			this.themeResource?.metaData?.revision ||
			this.themeResource?.descriptor?.lastModified ||
			Date.now()
		}`;

		url.searchParams.set("v", encodeURIComponent(version));

		return url.href;
	}

	/**
	 * Disposes the provider
	 */
	public dispose(): void {
		this.clearThemeResource();

		removeAllEventListeners(this.onChange);
		offEvent(this.appConfigSvc.onChange, this.handleAppConfigChange);
	}
}
